<template>
  <div class="dashboard">
    <TopNav/>
    <div class="interval-filter border-bottom">
      <div class="container">
        <div class="columns">
          <!-- Date picker -->
          <div class="column is-4 date-picker">
            <b-datepicker
              v-model="date"
              placeholder="Select a start date"
              icon="calendar-today"
              :min-date="minDate"
              :max-date="maxDate"
              :readonly="false"
            >
              <button class="button is-success" @click="date = today">
                <b-icon icon="calendar-today"></b-icon>
                <span>Today</span>
              </button>
              <button class="button is-info m2 date-picker-footer-button" @click="date = null">
                <b-icon icon="close"></b-icon>
                <span>Clear</span>
              </button>
            </b-datepicker>
          </div>
          <!-- End of date picker -->
          <!-- Interval picker -->
          <div class="column is-2 is-offset-8">
            <div class="interval-picker">
              <b-dropdown hoverable>
                <button class="button is-mycolour" slot="trigger">
                  <span>{{ timeIntervalText }}</span>
                  <b-icon icon="menu-down"></b-icon>
                </button>
                <div v-for="(option, index) in rateTitles" :key="index">
                  <b-dropdown-item
                    v-on:click="durationSelection = index"
                    v-bind:class="{'is-active': index === durationSelection}"
                  >{{ option }}</b-dropdown-item>
                </div>
              </b-dropdown>
            </div>
          </div>
          <!-- End of interval picker -->
        </div>
      </div>
    </div>
    <div class="section">
      <!-- Chart -->
      <div class="columns">
        <div class="column is-full">
          <p class="is-size-5">
            <b>Function selected:</b>
            {{ name }}
          </p>
          <br>
          <div v-if="isLoading" class="columns is-centered">
            <LoadingAnimation/>
          </div>
          <div v-else-if="!this.samples || this.samples.length <= 1">
            <div class="card notenoughdata">
              <div class="card-content">
                <div class="content">Not enough data for line chart</div>
              </div>
            </div>
          </div>
          <div v-else>
            <LineChart :chartData="chartData" :options="chartOptions"/>
          </div>
        </div>
      </div>
      <!-- End of linechart -->
      <!-- Timeline -->
      <div class="columns">
        <div class="column is-full">
          <div v-if="isLoading" class="columns is-centered">
            <LoadingAnimation/>
          </div>

          <div v-else-if="!this.deployHistory || this.deployHistory.length < 1" class="is-centered">
            <div class="card notenoughdata">
              <div class="card-content">
                <div class="content">Not enough data for timeline</div>
              </div>
            </div>
          </div>

          <div v-else>
            <div class="timeline is-centered">
              <header class="timeline-header">
                <span class="tag is-medium is-success">{{ timelineStart }}</span>
              </header>
              <div
                class="timeline-item"
                v-for="(deployHist, index) in deployHistoryReversed"
                :key="index"
              >
                <div class="timeline-marker is-secondary is-icon">
                  <i class="fa fa-plus"></i>
                </div>
                <div class="timeline-content">
                  <p class="heading">{{ getFormattedDeployHistoryTime(index) }}</p>
                  <p>Rank: {{ deployHist.fn.rank }}</p>
                  <p>Percentage: {{ deployHist.fn.percentage | percentage }}</p>
                  <p>
                    Commit Sha1:
                    <code>{{ deployHist.deploy.commit.sha1 }}</code>
                  </p>
                </div>
              </div>
              <div class="timeline-header">
                <span class="tag is-medium is-success">{{ timelineEnd }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- End of Timeline -->
    </div>
  </div>
</template>

<script>
import { gql } from "apollo-boost";
import LineChart from "@/components/LineChart";
import TopNav from "@/components/TopNav.vue";
import LoadingAnimation from "../components/LoadingAnimation.vue";

var moment = require("moment");

export default {
  name: "HothpathDrilldown",
  components: {
    LineChart,
    TopNav,
    LoadingAnimation
  },
  apollo: {
    sampleHistory: {
      query: gql`
        query sampleHistory($query: SampleHistoryQuery!) {
          sampleHistory(query: $query) {
            timestamp
            sample
          }
        }
      `,
      variables() {
        return {
          query: {
            fnName: this.name,
            since: this.dateFrame,
            duration: this.currentDuration
          }
        };
      },
      watchLoading(isLoading) {
        this.isLoading = isLoading;
      }
    },
    deployHistory: {
      query: gql`
        query deployHistory($query: DeployHistoryQuery!) {
          deployHistory(query: $query) {
            fn {
              rank
              percentage
            }
            deploy {
              time
              commit {
                sha1
              }
            }
          }
        }
      `,
      variables() {
        return {
          query: {
            fnName: this.name,
            since: this.dateFrame,
            duration: this.currentDuration
          }
        };
      }
    }
  },
  props: {
    name: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      date: null,
      today: moment().toDate(),
      minDate: moment()
        .subtract(2, "M")
        .toDate(),
      maxDate: moment().toDate(),
      rateTitles: ["Every Minute", "Hourly", "Daily"],
      durationOptions: ["1m", "1h", "1d"],
      durationSelection: -1,
      sampleHistory: [],
      deployHistory: [],
      isLoading: true
    };
  },
  methods: {
    getFormattedDeployHistoryTime: function(index) {
      return moment(this.deployHistoryReversed[index].deploy.time).format(
        "MMMM DD YYYY, h:mm:ssa"
      );
    }
  },
  computed: {
    deployHistoryReversed() {
      return this.deployHistory.slice(0).reverse();
    },
    dateFrame() {
      return this.date == null
        ? moment()
            .subtract(7, "days")
            .format()
        : moment(this.date).format();
    },
    labels() {
      return this.sampleHistory.map(sample => {
        return moment(sample.timestamp).format("MM/DD/YYYY h:mm:ssa");
      });
    },
    samples() {
      return this.sampleHistory.map(fn => {
        return fn.sample;
      });
    },
    timeIntervalText() {
      return this.durationSelection < 0
        ? "Select Graph Rate"
        : this.rateTitles[this.durationSelection];
    },
    chartIntervalText() {
      return this.durationSelection < 0
        ? this.rateTitles[0]
        : this.rateTitles[this.durationSelection];
    },
    currentDuration() {
      return this.durationSelection < 0
        ? this.durationOptions[0]
        : this.durationOptions[this.durationSelection];
    },
    timelineStart() {
      return moment(this.deployHistoryReversed[0].deploy.time).format("MMMM");
    },
    timelineEnd() {
      return moment(
        this.deployHistoryReversed[this.deployHistoryReversed.length - 1].deploy
          .time
      ).format("MMMM");
    },
    chartOptions() {
      return {
        legend: {
          display: false
        },
        responsive: true,
        maintainAspectRatio: false,
        title: {
          text: "Function Activity " + this.chartIntervalText,
          fontSize: 18,
          display: true
        }
      };
    },
    chartData() {
      return {
        labels: this.labels,
        datasets: [
          {
            label: "Function Activity " + this.chartIntervalText,
            backgroundColor: "#eaf7ff",
            data: this.samples
          }
        ]
      };
    }
  }
};
</script>

<style>
.dashboard {
  background: #fafcff;
  min-height: 100vh;
  height: auto;
}
.notenoughdata {
  margin: 0 auto;
  width: 320px;
}
.interval-filter {
  width: 100%;
  height: 70px;
  background: white;
}
.interval-picker {
  float: right;
  margin-top: 10px;
  padding-right: 10px;
}
.border-bottom {
  border-bottom: 1px solid #dee2e6 !important;
}
.is-mycolour {
  background-color: #0091d5;
  color: white;
}
.is-mycolour:hover {
  background-color: #0091d5;
  color: white;
}
.is-active {
  background-color: #0091d5;
  color: white;
}
.date-picker {
  padding-left: 20px;
  margin-top: 10px;
  max-width: 220px;
  float: left;
}
.date-picker-border {
  border: 1px solid #0091d5;
  border-radius: 5px;
}

.date-picker-footer-button {
  margin-left: 5px;
}
</style>
